import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../api/ApiProvider.js';
import styles from './CreateClassroomForm.module.css';

function defaultSchoolYear(now = new Date()): string {
  // School year flips in July (heuristic). Aug 2025 -> "2025-26", March 2026 -> "2025-26".
  const month = now.getMonth() + 1;
  const fullYear = now.getFullYear();
  const startYear = month >= 7 ? fullYear : fullYear - 1;
  const endShort = String((startYear + 1) % 100).padStart(2, '0');
  return `${startYear}-${endShort}`;
}

export interface CreateClassroomFormProps {
  /** Fired after the create mutation succeeds. The dialog uses this to
   *  close itself once the new classroom lands. */
  onCreated?: () => void;
  onCancel?: () => void;
}

export function CreateClassroomForm({ onCreated, onCancel }: CreateClassroomFormProps = {}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [schoolYear, setSchoolYear] = useState(defaultSchoolYear());

  const mutation = useMutation({
    mutationFn: (input: { name: string; schoolYear: string }) => api.classrooms.create(input),
    onSuccess: () => {
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['classrooms'] });
      onCreated?.();
    },
  });

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Read the values straight from the form's fields rather than React state.
    // Browser autofill / password managers can set an input's value without
    // firing React's onChange, leaving the controlled state empty while the box
    // looks filled — which made this submit silently do nothing (the create
    // never fired: no spinner, no error, no close). FormData reflects what's
    // actually on screen, so the create always runs with the visible values.
    const data = new FormData(e.currentTarget);
    const nextName = String(data.get('name') ?? '').trim();
    const nextYear = String(data.get('schoolYear') ?? '').trim();
    if (!nextName || !nextYear) return;
    mutation.mutate({ name: nextName, schoolYear: nextYear });
  };

  return (
    <form
      onSubmit={submit}
      className={`create-classroom-form ${styles.form}`}
      data-testid="create-classroom-form"
    >
      <label className={styles.field}>
        <span className={styles.label}>Name</span>
        <input
          type="text"
          name="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="Math 5"
          className={styles.input}
          data-testid="classroom-name-input"
          autoFocus
        />
      </label>
      <label className={styles.field}>
        <span className={styles.label}>School year</span>
        <input
          type="text"
          name="schoolYear"
          value={schoolYear}
          onChange={(e) => setSchoolYear(e.target.value)}
          required
          placeholder="2025-26"
          className={styles.input}
          data-testid="classroom-year-input"
        />
      </label>
      {mutation.isError ? (
        <p role="alert" className={styles.error}>
          {mutation.error instanceof Error ? mutation.error.message : 'Failed to create.'}
        </p>
      ) : null}
      <div className={styles.actions}>
        {onCancel ? (
          <button
            type="button"
            className={styles.cancel}
            onClick={onCancel}
            data-testid="classroom-create-cancel"
          >
            Cancel
          </button>
        ) : null}
        <button
          type="submit"
          className="glass-pill glass-pill--accent"
          disabled={mutation.isPending}
          data-testid="classroom-create-button"
        >
          {mutation.isPending ? 'Creating…' : 'Create classroom'}
        </button>
      </div>
    </form>
  );
}
